import { useState, useEffect } from 'react';
import { financeService, Invoice, Payment, FinancialSummary } from '../../services/finance.service';
import { useAuth } from '../../contexts/AuthContext';

export function FinancePage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  // Estado del formulario de factura - usando snake_case
  const [invoiceForm, setInvoiceForm] = useState<Partial<Invoice>>({
    number: '',
    client_id: '',
    project_id: '',
    date_issued: Date.now(),
    date_due: Date.now() + 30 * 24 * 60 * 60 * 1000,
    subtotal: 0,
    tax: 0,
    total: 0,
    discount: 0,
    status: 'pending',
    notes: ''
  });

  // Estado del formulario de pago - usando snake_case
  const [paymentForm, setPaymentForm] = useState<Partial<Payment>>({
    invoice_id: '',
    amount: 0,
    method: 'transfer',
    date_paid: Date.now(),
    reference: '',
    notes: ''
  });

  useEffect(() => {
    loadFinanceData();
  }, []);

  const loadFinanceData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [invoicesData, paymentsData, summaryData] = await Promise.all([
        financeService.getInvoices(),
        financeService.getPayments(),
        financeService.getFinancialSummary()
      ]);
      setInvoices(invoicesData);
      setPayments(paymentsData);
      setSummary(summaryData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos financieros');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await financeService.createInvoice(invoiceForm);
      await loadFinanceData();
      setShowInvoiceModal(false);
      resetInvoiceForm();
    } catch (err: any) {
      setError(err.message || 'Error al crear factura');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingInvoice) return;
    setIsLoading(true);
    setError('');

    try {
      await financeService.updateInvoice(editingInvoice.id, invoiceForm);
      await loadFinanceData();
      setShowInvoiceModal(false);
      resetInvoiceForm();
    } catch (err: any) {
      setError(err.message || 'Error al actualizar factura');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await financeService.createPayment(paymentForm);
      await loadFinanceData();
      setShowPaymentModal(false);
      resetPaymentForm();
    } catch (err: any) {
      setError(err.message || 'Error al registrar pago');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (id: string, status: Invoice['status']) => {
    try {
      await financeService.updateInvoiceStatus(id, status);
      await loadFinanceData();
    } catch (err: any) {
      setError(err.message || 'Error al actualizar estado');
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      number: '',
      client_id: '',
      project_id: '',
      date_issued: Date.now(),
      date_due: Date.now() + 30 * 24 * 60 * 60 * 1000,
      subtotal: 0,
      tax: 0,
      total: 0,
      discount: 0,
      status: 'pending',
      notes: ''
    });
    setEditingInvoice(null);
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      invoice_id: '',
      amount: 0,
      method: 'transfer',
      date_paid: Date.now(),
      reference: '',
      notes: ''
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { class: string; label: string }> = {
      paid: { class: 'status-completed', label: 'Pagada' },
      pending: { class: 'status-pending', label: 'Pendiente' },
      sent: { class: 'status-progress', label: 'Enviada' },
      overdue: { class: 'status-inactive', label: 'Vencida' },
      cancelled: { class: 'status-pending', label: 'Cancelada' }
    };
    return badges[status] || { class: 'status-pending', label: status };
  };

  const getStatusOptions = () => {
    return [
      { value: 'pending', label: 'Pendiente' },
      { value: 'sent', label: 'Enviada' },
      { value: 'paid', label: 'Pagada' },
      { value: 'overdue', label: 'Vencida' },
      { value: 'cancelled', label: 'Cancelada' }
    ];
  };

  const getPaymentMethodLabel = (method: string) => {
    const methods: Record<string, string> = {
      transfer: 'Transferencia',
      cash: 'Efectivo',
      card: 'Tarjeta',
      check: 'Cheque'
    };
    return methods[method] || method;
  };

  if (isLoading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando datos financieros...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-gray-600">Gestión financiera del despacho</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              resetInvoiceForm();
              setShowInvoiceModal(true);
            }}
            className="btn btn-primary"
          >
            + Nueva Factura
          </button>
          <button
            onClick={() => {
              resetPaymentForm();
              setShowPaymentModal(true);
            }}
            className="btn btn-secondary"
          >
            + Registrar Pago
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      {/* Resumen Financiero */}
      {summary && (
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-icon">💰</span>
            <span className="stat-value">{formatCurrency(summary.totalRevenue)}</span>
            <span className="stat-label">Ingresos Totales</span>
          </div>
          <div className="stat-card">
            <span className="stat-icon">📊</span>
            <span className="stat-value">{formatCurrency(summary.netProfit)}</span>
            <span className="stat-label">Utilidad Neta</span>
          </div>
          <div className="stat-card">
            <span className="stat-icon">📋</span>
            <span className="stat-value">{summary.pendingInvoices}</span>
            <span className="stat-label">Facturas Pendientes</span>
          </div>
          <div className="stat-card">
            <span className="stat-icon">⏰</span>
            <span className="stat-value">{summary.overdueInvoices}</span>
            <span className="stat-label">Facturas Vencidas</span>
          </div>
        </div>
      )}

      {/* Facturas */}
      <div className="card">
        <h3>📄 Facturas</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Cliente</th>
                <th>Proyecto</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    No hay facturas registradas
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  const badge = getStatusBadge(invoice.status);
                  return (
                    <tr key={invoice.id}>
                      <td className="font-medium">{invoice.number}</td>
                      <td>{invoice.client_name || invoice.client_id.slice(0, 8)}</td>
                      <td>{invoice.project_name || invoice.project_id?.slice(0, 8) || '-'}</td>
                      <td className="font-bold text-primary">{formatCurrency(invoice.total)}</td>
                      <td>
                        <span className={`status-badge ${badge.class}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td>{formatDate(invoice.date_issued)}</td>
                      <td>
                        <div className="flex gap-2 flex-wrap">
                          <select
                            value={invoice.status}
                            onChange={(e) => handleUpdateStatus(invoice.id, e.target.value as Invoice['status'])}
                            className="px-2 py-1 border border-gray-300 rounded text-xs"
                          >
                            {getStatusOptions().map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => {
                              setEditingInvoice(invoice);
                              setInvoiceForm(invoice);
                              setShowInvoiceModal(true);
                            }}
                            className="btn-action"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('¿Seguro que quieres eliminar esta factura?')) {
                                financeService.deleteInvoice(invoice.id).then(loadFinanceData);
                              }
                            }}
                            className="btn-action text-red-600"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagos Recientes */}
      <div className="card">
        <h3>💳 Pagos Recientes</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Factura</th>
                <th>Monto</th>
                <th>Método</th>
                <th>Referencia</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    No hay pagos registrados
                  </td>
                </tr>
              ) : (
                payments.slice(0, 10).map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.invoice_number || payment.invoice_id.slice(0, 8)}</td>
                    <td className="font-bold text-green-600">{formatCurrency(payment.amount)}</td>
                    <td>
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {getPaymentMethodLabel(payment.method)}
                      </span>
                    </td>
                    <td>{payment.reference || '-'}</td>
                    <td>{formatDate(payment.date_paid)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================
          MODAL DE FACTURA
          ============================================================ */}
      {showInvoiceModal && (
        <div className="modal-overlay" onClick={() => setShowInvoiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingInvoice ? '✏️ Editar Factura' : '➕ Nueva Factura'}</h3>
              <button
                onClick={() => {
                  setShowInvoiceModal(false);
                  resetInvoiceForm();
                }}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <form onSubmit={editingInvoice ? handleUpdateInvoice : handleCreateInvoice}>
              <div className="modal-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label>Número de factura</label>
                    <input
                      type="text"
                      required
                      value={invoiceForm.number || ''}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, number: e.target.value })}
                      placeholder="F-2024-001"
                    />
                  </div>

                  <div className="form-group">
                    <label>Estado</label>
                    <select
                      value={invoiceForm.status || 'pending'}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, status: e.target.value as Invoice['status'] })}
                    >
                      {getStatusOptions().map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label>Cliente ID</label>
                    <input
                      type="text"
                      required
                      value={invoiceForm.client_id || ''}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, client_id: e.target.value })}
                      placeholder="ID del cliente"
                    />
                  </div>

                  <div className="form-group">
                    <label>Proyecto ID</label>
                    <input
                      type="text"
                      value={invoiceForm.project_id || ''}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, project_id: e.target.value })}
                      placeholder="ID del proyecto (opcional)"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label>Fecha de emisión</label>
                    <input
                      type="date"
                      required
                      value={invoiceForm.date_issued ? new Date(invoiceForm.date_issued).toISOString().split('T')[0] : ''}
                      onChange={(e) => setInvoiceForm({
                        ...invoiceForm,
                        date_issued: e.target.value ? new Date(e.target.value).getTime() : Date.now()
                      })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Fecha de vencimiento</label>
                    <input
                      type="date"
                      required
                      value={invoiceForm.date_due ? new Date(invoiceForm.date_due).toISOString().split('T')[0] : ''}
                      onChange={(e) => setInvoiceForm({
                        ...invoiceForm,
                        date_due: e.target.value ? new Date(e.target.value).getTime() : Date.now() + 30 * 24 * 60 * 60 * 1000
                      })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label>Subtotal</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={invoiceForm.subtotal || 0}
                      onChange={(e) => {
                        const subtotal = parseFloat(e.target.value) || 0;
                        const tax = subtotal * 0.16;
                        const total = subtotal + tax - (invoiceForm.discount || 0);
                        setInvoiceForm({ ...invoiceForm, subtotal, tax, total });
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>IVA (16%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={invoiceForm.tax || 0}
                      disabled
                      className="bg-gray-100"
                    />
                  </div>

                  <div className="form-group">
                    <label>Total</label>
                    <input
                      type="number"
                      step="0.01"
                      value={invoiceForm.total || 0}
                      disabled
                      className="bg-gray-100 font-bold text-primary"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Notas</label>
                  <textarea
                    rows={2}
                    value={invoiceForm.notes || ''}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                    placeholder="Notas adicionales..."
                    className="resize-none"
                  />
                </div>

                {error && (
                  <div className="error-message">
                    <span className="error-icon">❌</span>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    resetInvoiceForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" disabled={isLoading} className="btn btn-primary">
                  {isLoading ? 'Guardando...' : editingInvoice ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================
          MODAL DE PAGO
          ============================================================ */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Registrar Pago</h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  resetPaymentForm();
                }}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreatePayment}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Factura ID</label>
                  <input
                    type="text"
                    required
                    value={paymentForm.invoice_id || ''}
                    onChange={(e) => setPaymentForm({ ...paymentForm, invoice_id: e.target.value })}
                    placeholder="ID de la factura"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label>Monto</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={paymentForm.amount || 0}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Método de pago</label>
                    <select
                      value={paymentForm.method || 'transfer'}
                      onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value as Payment['method'] })}
                    >
                      <option value="transfer">Transferencia</option>
                      <option value="cash">Efectivo</option>
                      <option value="card">Tarjeta</option>
                      <option value="check">Cheque</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Fecha de pago</label>
                  <input
                    type="date"
                    required
                    value={paymentForm.date_paid ? new Date(paymentForm.date_paid).toISOString().split('T')[0] : ''}
                    onChange={(e) => setPaymentForm({
                      ...paymentForm,
                      date_paid: e.target.value ? new Date(e.target.value).getTime() : Date.now()
                    })}
                  />
                </div>

                <div className="form-group">
                  <label>Referencia</label>
                  <input
                    type="text"
                    value={paymentForm.reference || ''}
                    onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                    placeholder="Número de referencia o folio"
                  />
                </div>

                <div className="form-group">
                  <label>Notas</label>
                  <textarea
                    rows={2}
                    value={paymentForm.notes || ''}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder="Notas adicionales..."
                    className="resize-none"
                  />
                </div>

                {error && (
                  <div className="error-message">
                    <span className="error-icon">❌</span>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    resetPaymentForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" disabled={isLoading} className="btn btn-primary">
                  {isLoading ? 'Registrando...' : 'Registrar Pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}